"use client";

import { useEffect } from "react";

import type { Article } from "../../types/content";

import { imageSrc } from "../../utils/image";
import { updateClientMetaTag } from "../../utils/updateClientMetaTag";
import { getArticleSlug } from "../../utils/getArticleSlug";

const ArticleClientMetaData = ({ content }: { content: Article }) => {
  useEffect(() => {
    const updateArticleMetadata = () => {
      const isEconomy = process.env.NEXT_PUBLIC_UI_THEME === "ekonomi";
      const firstParagraph =
        content.resources.find((resource) => resource.type === "Text")
          ?.paragraphs?.[0]?.text?.value || "";
      const articleMainImageResource = content.resources.find(
        (resource) => resource.type === "Image",
      );
      const articleImage = articleMainImageResource
        ? {
            url: imageSrc(
              articleMainImageResource?.format || "large",
              true,
              articleMainImageResource,
            ),
            width: 1372,
            height: 708,
            alt: articleMainImageResource?.caption?.value || "",
          }
        : null;

      const slug = getArticleSlug(content);
      const url = isEconomy
        ? `https://omniekonomi.se/${slug}/a/${content.article_id}`
        : `https://omni.se/${slug}/a/${content.article_id}`;
      const authors =
        content.authors?.map((author) => author.title).join(", ") || "";

      const title =
        content?.meta.custom_properties?.seo_title || content?.title.value;

      document.title = title;

      updateClientMetaTag(
        "description",
        firstParagraph || content?.title.value,
      );

      updateClientMetaTag("og:title", title, "property");
      updateClientMetaTag("og:url", url, "property");
      updateClientMetaTag(
        "og:site_name",
        isEconomy ? "Omni Ekonomi" : "Omni",
        "property",
      );
      updateClientMetaTag("og:type", "article", "property");
      updateClientMetaTag("og:locale", "sv_SE", "property");
      updateClientMetaTag("og:authors", authors, "property");
      updateClientMetaTag(
        "og:published_time",
        content.changes.published,
        "property",
      );
      updateClientMetaTag(
        "og:modified_time",
        content.changes.modified,
        "property",
      );
      updateClientMetaTag("og:section", content.category.title, "property");
      updateClientMetaTag(
        "og:tags",
        content.tags.map((tag) => tag.title).join(", "),
        "property",
      );
      if (articleImage) {
        updateClientMetaTag("og:image", articleImage.url, "property");
      }

      updateClientMetaTag("twitter:title", title, "name");
      updateClientMetaTag(
        "twitter:site",
        isEconomy ? "@OmniEkonomi" : "@omni_red",
        "name",
      );
      updateClientMetaTag(
        "twitter:creator",
        isEconomy ? "@OmniEkonomi" : "@omni_red",
        "name",
      );
      if (articleImage) {
        updateClientMetaTag("twitter:image", articleImage.url, "name");
      }

      updateClientMetaTag("canonical", url, "property");

      if (content.meta.is_sponsored) {
        updateClientMetaTag("robots", "noindex, nofollow, noarchive");
      }
    };

    const shouldUpdateMetaData =
      content?.title?.value && document.title !== content.title.value;
    if (shouldUpdateMetaData) {
      updateArticleMetadata();
    }
  }, [content]);

  return null;
};

export { ArticleClientMetaData };
